import React, { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { auth, db, storage } from "../../../firebaseConfig";

export const COLLECTIONS = Object.freeze({
  projects: "godown_projects",
  users: "nafed_users",
  activity: "nafed_activity",
  notifications: "nafed_notifications",
  mail: "mail",
});

export const STATE_CHECKLIST = Object.freeze([
  ["registration", "Registration certificate and society profile"],
  ["bylaws", "Registered bye-laws and amendments"],
  ["financials", "Last three-year financial statements / balance sheets"],
  ["audits", "Last three-year audit reports and audit memos"],
  ["resolutions", "Five signed PACS resolutions / declarations"],
  ["land", "Land title or registered lease documents"],
  ["estimate", "PMC preliminary estimate / technical note"],
  ["assurance", "NAFED assurance / supporting letter"],
  ["district", "District scrutiny, recommendation and dispatch record"],
  ["suoMoto", "Clear suo-moto / adverse-status verification"],
]);

export const WORKFLOW = Object.freeze([
  "PACS Package",
  "PMC Forward",
  "District Scrutiny",
  "State Scrutiny",
  "DCDC Decision",
  "Process Complete",
]);

export const upper = (value = "") =>
  String(value || "")
    .trim()
    .toUpperCase();

export const roleKey = (value = "") =>
  upper(value).replace(/[\s-]+/g, "_");

export const normal = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

export const recordState = (record = {}) =>
  record?.state ||
  record?.stateName ||
  record?.projectState ||
  record?.pacsState ||
  record?.jurisdictionState ||
  record?.applicationData?.state ||
  record?.applicationData?.stateName ||
  record?.formData?.state ||
  record?.formData?.stateName ||
  record?.projectDetails?.state ||
  "";

export const recordDistrict = (record = {}) =>
  record?.district ||
  record?.districtName ||
  record?.projectDistrict ||
  record?.pacsDistrict ||
  record?.jurisdictionDistrict ||
  record?.applicationData?.district ||
  record?.applicationData?.districtName ||
  record?.formData?.district ||
  record?.formData?.districtName ||
  record?.projectDetails?.district ||
  "";

export const human = (value = "-") =>
  String(value || "-")
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

export const number = (value) => Number(value || 0).toLocaleString("en-IN");

export const toDate = (value) => {
  if (!value) return null;
  const parsed = value?.toDate
    ? value.toDate()
    : value?.seconds
      ? new Date(value.seconds * 1000)
      : new Date(value);
  return Number.isNaN(parsed?.getTime?.()) ? null : parsed;
};

export const timeOf = (value) => toDate(value)?.getTime?.() || 0;

export const dateTime = (value) => {
  const parsed = toDate(value);
  return parsed
    ? parsed.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";
};

export const dateOnly = (value) => {
  const parsed = toDate(value);
  return parsed
    ? parsed.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";
};

export const projectId = (project = {}) =>
  project.applicationId ||
  project.projectId ||
  project.registrationNo ||
  project.applicationData?.applicationId ||
  project.applicationData?.projectId ||
  project.formData?.applicationId ||
  project.id ||
  "-";

export const projectName = (project = {}) =>
  project.pacsName ||
  project.societyName ||
  project.organizationName ||
  project.projectName ||
  project.applicationData?.pacsName ||
  project.applicationData?.societyName ||
  project.formData?.pacsName ||
  project.formData?.societyName ||
  "PACS / Society";

export const capacityMt = (project = {}) =>
  Number(
    project.capacityMt ||
      project.capacityMT ||
      project.capacity ||
      project.godownCapacity ||
      project.storageCapacity ||
      project.proposedCapacityMt ||
      project.applicationData?.capacityMt ||
      project.applicationData?.proposedCapacityMt ||
      project.formData?.capacityMt ||
      project.formData?.proposedCapacityMt ||
      0,
  );

export const locationText = (project = {}) =>
  [
    project.village || project.villageName,
    project.taluka || project.talukaName || project.block,
    recordDistrict(project),
    recordState(project),
  ]
    .filter(Boolean)
    .join(", ") || "-";

export const isDeleted = (record = {}) =>
  record.deleted === true ||
  record.isDeleted === true ||
  Boolean(record.deletedAt) ||
  ["DELETED", "REMOVED", "ARCHIVED", "TRASHED"].includes(upper(record.status));

export const isNafedProject = (record = {}) =>
  record.nafedProject === true ||
  upper(record.projectSource) === "NAFED" ||
  upper(record.applicationPortal) === "NAFED_PACS_APPLY" ||
  upper(record.portal) === "NAFED" ||
  record.applicationData?.nafedProject === true ||
  upper(record.applicationData?.projectSource) === "NAFED" ||
  upper(record.applicationData?.applicationPortal) === "NAFED_PACS_APPLY";

export const districtStatus = (project = {}) =>
  upper(
    project.registrarWorkflow?.district?.status ||
      project.registrarWorkflow?.districtRegistrarStatus ||
      project.districtRegistrarStatus ||
      "WAITING_FOR_DISTRICT_FILE",
  );

export const stateStatus = (project = {}) =>
  upper(
    project.registrarWorkflow?.state?.status ||
      project.registrarWorkflow?.stateRegistrarStatus ||
      project.stateProjectPermissionStatus ||
      project.stateRegistrarStatus ||
      "WAITING_FOR_DISTRICT_FORWARD",
  );

export const subsidyRecord = (project = {}) =>
  project.subsidyFirstInstallment || {};

export const subsidyPackage = (project = {}) =>
  subsidyRecord(project).package || project.subsidyPackage || {};

export const hasSubsidyFile = (project = {}) => {
  const documents = subsidyRecord(project).documents || {};
  return Boolean(
    subsidyPackage(project)?.url ||
      Object.values(documents).some((item) => item?.url) ||
      project.subsidyFirstInstallmentStage ||
      project.subsidyWorkflowStatus,
  );
};

export const registrarPackageUrl = (project = {}) =>
  project.registrarPackage?.url ||
  project.registrarDocumentSnapshot?.package?.url ||
  project.registrarPackageUrl ||
  "";

export const hasRegistrarPackage = (project = {}) =>
  Boolean(
    registrarPackageUrl(project) ||
      upper(project.registrarFileStage).includes("READY_FOR_PMC") ||
      upper(project.registrarFileStage).includes("READY_FOR_DISTRICT") ||
      upper(project.physicalFileStatus).includes("COMPLETE"),
  );

export const pmcRegistrarStatus = (project = {}) =>
  upper(
    project.registrarWorkflow?.pmc?.status ||
      project.pmcRegistrarStatus ||
      (hasRegistrarPackage(project)
        ? "WAITING_FOR_PMC_FORWARD"
        : "PACS_PACKAGE_PENDING"),
  );

export const isPmcForwardedToDistrict = (project = {}) => {
  const status = pmcRegistrarStatus(project);
  return Boolean(
    project.pmcRegistrarFileForwardedToDistrict === true ||
      status.includes("FORWARDED_TO_DISTRICT") ||
      status.includes("COMPLIANCE_RESUBMITTED_TO_DISTRICT") ||
      upper(project.registrarWorkflowStage).includes(
        "PMC_FILE_FORWARDED_TO_DISTRICT",
      ),
  );
};

export const isDistrictComplianceReceived = (project = {}) =>
  pmcRegistrarStatus(project).includes("COMPLIANCE_RESUBMITTED_TO_DISTRICT") ||
  districtStatus(project).includes("PMC_COMPLIANCE_RESUBMITTED") ||
  districtStatus(project).includes("PMC_STATE_QUERY_COMPLIANCE_RESUBMITTED");

export const isForwardedToState = (project = {}) => {
  const status = districtStatus(project);
  return Boolean(
      project.districtPhysicalFileForwardedToState === true ||
      status.includes("FORWARDED_TO_STATE") ||
      status.includes("COMPLIANCE_FORWARDED_TO_STATE") ||
      status.includes("RECOMMENDED_TO_STATE") ||
      upper(project.registrarWorkflowStage).includes("PENDING_STATE") ||
      project.stateRegistrarPhysicalFileReceived === true,
  );
};

export const isStateApproved = (project = {}) => {
  const status = stateStatus(project);
  return Boolean(
    project.stateProjectPermissionGranted === true ||
      project.stateRegistrarApprovedForDcdc === true ||
      status.includes("PERMISSION_GRANTED") ||
      status.includes("APPROVED_FOR_DCDC") ||
      upper(project.registrarWorkflowStage).includes(
        "STATE_APPROVED_RETURNED_TO_DISTRICT_FOR_DCDC",
      ) ||
      project.dcdcStatus ||
      project.dcdcMeetingDate,
  );
};

export const isDistrictQuery = (project = {}) =>
  districtStatus(project).includes("QUERY");

export const isStateQuery = (project = {}) =>
  stateStatus(project).includes("QUERY");

export const stageKey = (project = {}) => {
  if (hasSubsidyFile(project)) return "SUBSIDY";
  if (project.dcdcStatus || project.dcdcMeetingDate) return "DCDC";
  if (isStateApproved(project)) return "STATE_APPROVED";
  if (isStateQuery(project)) return "STATE_QUERY";
  if (project.stateRegistrarChecklistComplete === true)
    return "STATE_PERMISSION_PENDING";
  if (project.stateRegistrarPhysicalFileReceived === true)
    return "STATE_SCRUTINY";
  if (isForwardedToState(project)) return "STATE_INBOX";
  if (
    stateStatus(project).includes("RETURNED_TO_DISTRICT") ||
    districtStatus(project).includes("STATE_QUERY")
  )
    return "STATE_QUERY";
  if (isDistrictQuery(project)) return "DISTRICT_QUERY";
  if (
    project.districtRegistrarVerified === true ||
    districtStatus(project).includes("VERIFIED")
  )
    return "DISTRICT_FORWARD_READY";
  if (project.districtPhysicalFileReceived === true) return "DISTRICT_SCRUTINY";
  if (isPmcForwardedToDistrict(project)) return "DISTRICT_INBOX";
  if (hasRegistrarPackage(project)) return "PMC_FORWARD_PENDING";
  return "PACS_PREPARATION";
};

export const stageLabel = (project = {}) =>
  human(
    {
      PACS_PREPARATION: "PACS File Preparation",
      PMC_FORWARD_PENDING: "Ready for PMC Forwarding",
      DISTRICT_INBOX: "District File Inbox",
      DISTRICT_SCRUTINY: "District Scrutiny",
      DISTRICT_QUERY: "District Query",
      DISTRICT_FORWARD_READY: "Ready for State Forwarding",
      STATE_INBOX: "State File Inbox",
      STATE_SCRUTINY: "State Document Scrutiny",
      STATE_QUERY: "State Query",
      STATE_PERMISSION_PENDING: "Permission Order Pending",
      STATE_APPROVED: "State Permission Granted",
      DCDC: "DCDC Processing",
      SUBSIDY: "Work / Subsidy File",
    }[stageKey(project)] || stageKey(project),
  );

export const workflowIndex = (project = {}) => {
  if (["APPROVED", "REJECTED"].includes(upper(project.dcdcStatus))) return 5;
  if (project.dcdcStatus || project.dcdcMeetingDate || isStateApproved(project))
    return 4;
  if (isForwardedToState(project) || isStateQuery(project)) return 3;
  if (isPmcForwardedToDistrict(project) || isDistrictQuery(project)) return 2;
  if (hasRegistrarPackage(project)) return 1;
  return 0;
};

const documentCategory = (path = "") => {
  const key = normal(path);
  if (/registration|certificate/.test(key)) return "Registration";
  if (/by.?law/.test(key)) return "Bye-laws";
  if (/balance|financial|surplus|profit/.test(key)) return "Financials";
  if (/audit/.test(key)) return "Audit";
  if (/resolution|declaration|certificate/.test(key)) return "Resolutions";
  if (/land|lease|7.?12|survey|khata|title/.test(key)) return "Land / Lease";
  if (/estimate|dpr|design|soil|technical/.test(key)) return "Technical";
  if (/mou|memorandum/.test(key)) return "Executed MOU";
  if (/assurance|nafed|supporting/.test(key)) return "NAFED Assurance";
  if (/district|dispatch|scrutiny/.test(key)) return "Registrar Record";
  if (/subsidy|instalment|installment/.test(key)) return "Subsidy";
  return "Official Document";
};

const deepValue = (source = {}, path = "") =>
  String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((value, key) => value?.[key], source);

const documentUrl = (value) => {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object") return "";
  return String(
    value.url ||
      value.downloadURL ||
      value.downloadUrl ||
      value.fileUrl ||
      value.pdfUrl ||
      "",
  ).trim();
};

const firstDocument = (project, paths = []) => {
  for (const path of paths) {
    const value = deepValue(project, path);
    const url = documentUrl(value);
    if (/^https?:\/\//i.test(url)) {
      return {
        url,
        name:
          value?.fileName ||
          value?.name ||
          value?.title ||
          human(path.split(".").pop()),
        source: path,
      };
    }
  }
  return null;
};

/**
 * Named authority-document register shared by PACS, PMC, District and State.
 * Every entry accepts the legacy aliases already used by the live portals.
 */
export const REGISTRAR_DOCUMENTS = Object.freeze([
  {
    key: "registrarPackage",
    label: "Complete PACS Registrar Package",
    owner: "PACS",
    required: true,
    paths: [
      "registrarPackage",
      "registrarPackageUrl",
      "registrarDocumentSnapshot.package",
    ],
  },
  {
    key: "finalDpr",
    label: "PMC Final Signed DPR",
    owner: "PMC",
    required: true,
    paths: [
      "finalSignedDprUrl",
      "finalDprUrl",
      "dprOriginalPdfUrl",
      "dprPdfUrl",
      "dprUrl",
      "manualDpr.finalPdf",
      "manualDpr.publishedPdf",
      "pmcTechnicalDocuments.PMC_FINAL_DPR",
      "pmcVerificationDocuments.PMC_FINAL_DPR",
    ],
  },
  {
    key: "executedMou",
    label: "Fully Executed PACS–PMC MOU",
    owner: "PACS & PMC",
    required: true,
    paths: [
      "finalExecutedMouUrl",
      "finalMouUrl",
      "finalSignedMouUrl",
      "executedMouUrl",
      "mouFinalPdfUrl",
      "mouDocuments.finalExecuted",
      "pmcVerificationDocuments.FULLY_EXECUTED_MOU",
    ],
  },
  {
    key: "assurance",
    label: "NAFED Assurance Letter",
    owner: "NAFED / PMC",
    required: true,
    paths: [
      "nafedAssuranceLetter",
      "nafedAssuranceLetterUrl",
      "hiringAssuranceLetterUrl",
      "assuranceLetterUrl",
      "pmcVerificationDocuments.NAFED_ASSURANCE_LETTER",
      "pmcTechnicalDocuments.NAFED_ASSURANCE_LETTER",
      "registrarDocuments.NAFED_ASSURANCE_LETTER",
    ],
  },
  {
    key: "districtForwarding",
    label: "District Scrutiny & Forwarding Letter",
    owner: "District Registrar",
    required: false,
    paths: [
      "districtForwardingLetterUrl",
      "districtScrutinyLetterUrl",
      "registrarWorkflow.district.forwardingLetter",
    ],
  },
  {
    key: "statePermission",
    label: "State Project Permission Order",
    owner: "State Registrar",
    required: false,
    paths: [
      "statePermissionOrderUrl",
      "stateScrutinyLetterUrl",
      "registrarWorkflow.state.permissionOrder",
    ],
  },
  {
    key: "dcdcDecision",
    label: "Signed DCDC Decision Letter",
    owner: "District DCDC",
    required: false,
    paths: ["dcdcDecisionLetterUrl", "registrarWorkflow.district.dcdcLetter"],
  },
  {
    key: "subsidyPackage",
    label: "Work-Start / Subsidy Package",
    owner: "PMC / PACS",
    required: false,
    paths: [
      "subsidyFirstInstallment.package",
      "subsidyPackage",
      "subsidyPackageUrl",
    ],
  },
]);

export function registrarDocumentMatrix(project = {}) {
  return REGISTRAR_DOCUMENTS.map((definition) => ({
    ...definition,
    document: firstDocument(project, definition.paths),
  }));
}

export function documentReadiness(project = {}) {
  const documents = registrarDocumentMatrix(project);
  const required = documents.filter((item) => item.required);
  const complete = required.filter((item) => item.document).length;
  return {
    complete,
    total: required.length,
    ready: complete === required.length,
    percent: required.length ? Math.round((complete / required.length) * 100) : 100,
  };
}

export function registrarAutomation(project = {}, scope = "DISTRICT") {
  const authority = upper(scope);
  let queue = "Monitoring";
  let nextAction = "Monitor live project movement";
  let actionKey = "overview";
  let actionable = false;

  if (authority === "DISTRICT") {
    if (isDistrictQuery(project)) {
      queue = "Compliance";
      nextAction = "Track PACS / PMC query compliance";
      actionKey = "queries";
    } else if (hasRegistrarPackage(project) && !project.districtPhysicalFileReceived) {
      queue = "Inward";
      nextAction = "Record physical file inward";
      actionKey = "inbox";
      actionable = true;
    } else if (
      project.districtPhysicalFileReceived &&
      project.districtRegistrarVerified !== true &&
      !districtStatus(project).includes("VERIFIED")
    ) {
      queue = "Scrutiny";
      nextAction = "Complete documents and adverse-status scrutiny";
      actionKey = "scrutiny";
      actionable = true;
    } else if (
      (project.districtRegistrarVerified === true ||
        districtStatus(project).includes("VERIFIED")) &&
      !isForwardedToState(project)
    ) {
      queue = "Dispatch";
      nextAction = "Upload letter and forward to State Registrar";
      actionKey = "forward";
      actionable = true;
    } else if (isStateApproved(project) && !project.dcdcMeetingDate) {
      queue = "DCDC";
      nextAction = "Schedule DCDC meeting";
      actionKey = "dcdc";
      actionable = true;
    } else if (
      isStateApproved(project) &&
      project.dcdcMeetingDate &&
      !["APPROVED", "REJECTED"].includes(upper(project.dcdcStatus))
    ) {
      queue = "DCDC";
      nextAction = "Record DCDC decision and signed letter";
      actionKey = "dcdc";
      actionable = true;
    }
  } else {
    if (isStateQuery(project)) {
      queue = "Compliance";
      nextAction = "Track District / PACS / PMC compliance";
      actionKey = "queries";
    } else if (
      isForwardedToState(project) &&
      !project.stateRegistrarPhysicalFileReceived
    ) {
      queue = "State Inward";
      nextAction = "Record State physical file inward";
      actionKey = "inbox";
      actionable = true;
    } else if (
      project.stateRegistrarPhysicalFileReceived &&
      project.stateRegistrarChecklistComplete !== true &&
      !isStateApproved(project)
    ) {
      queue = "10-Point Scrutiny";
      nextAction = "Complete mandatory State checklist";
      actionKey = "scrutiny";
      actionable = true;
    } else if (
      project.stateRegistrarChecklistComplete === true &&
      !isStateApproved(project)
    ) {
      queue = "Permission";
      nextAction = "Upload and issue signed permission order";
      actionKey = "permission";
      actionable = true;
    }
  }

  const ageDays = Math.max(
    0,
    Math.floor(
      (Date.now() - timeOf(project.updatedAt || project.createdAt)) / 86400000,
    ),
  );
  const priority = !actionable
    ? "NORMAL"
    : ageDays >= 7
      ? "CRITICAL"
      : ageDays >= 3
        ? "HIGH"
        : "DUE";
  return { queue, nextAction, actionKey, actionable, ageDays, priority };
}

export function collectDocuments(project = {}) {
  const documents = [];
  const seen = new Set();
  const add = (url, name, path) => {
    const link = String(url || "").trim();
    if (!/^https?:\/\//i.test(link) || seen.has(link)) return;
    seen.add(link);
    documents.push({
      url: link,
      name: String(name || human(path.split(".").pop()) || "Document"),
      category: documentCategory(path),
      path,
    });
  };
  const walk = (value, path = "project", depth = 0) => {
    if (depth > 5 || value == null) return;
    if (typeof value === "string") {
      if (/url|document|file|pdf|package|certificate|report|letter/i.test(path))
        add(value, human(path.split(".").pop()), path);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item, index) => walk(item, `${path}.${index}`, depth + 1));
      return;
    }
    if (typeof value !== "object") return;
    const directUrl =
      value.url || value.downloadURL || value.downloadUrl || value.fileUrl;
    if (directUrl)
      add(
        directUrl,
        value.fileName ||
          value.name ||
          value.title ||
          human(path.split(".").pop()),
        path,
      );
    Object.entries(value).forEach(([key, item]) => {
      if (["createdAt", "updatedAt", "timestamp"].includes(key)) return;
      walk(item, `${path}.${key}`, depth + 1);
    });
  };
  registrarDocumentMatrix(project).forEach((item) => {
    if (!item.document) return;
    add(
      item.document.url,
      item.label,
      `authority.${item.key}.${item.document.source}`,
    );
  });
  walk(project);
  return documents.sort((a, b) =>
    `${a.category}${a.name}`.localeCompare(`${b.category}${b.name}`),
  );
}

export function stateChecklistValue(project = {}) {
  const source =
    project.stateRegistrarChecklist ||
    project.registrarWorkflow?.state?.checklist ||
    {};
  return STATE_CHECKLIST.reduce(
    (result, [key]) => ({ ...result, [key]: source[key] === true }),
    {},
  );
}

export function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

export function useRegistrarSession(allowedRoles = []) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const roles = useMemo(
    () => allowedRoles.map(roleKey).sort().join("|"),
    [allowedRoles],
  );

  useEffect(() => {
    let stopProfile = () => {};
    let alive = true;
    const stopAuth = onAuthStateChanged(auth, async (currentUser) => {
      stopProfile();
      setUser(currentUser || null);
      setProfile(null);
      setError("");
      if (!currentUser) {
        setLoading(false);
        setError("Please sign in with an approved Registrar account.");
        return;
      }
      setLoading(true);
      const allowed = roles.split("|").filter(Boolean);
      const acceptProfile = (record) => {
        if (!alive) return;
        if (!record) {
          setProfile(null);
          setError("Registrar profile was not found in nafed_users.");
          setLoading(false);
          return;
        }
        const nextProfile = {
          ...record,
          state: recordState(record),
          district: recordDistrict(record),
        };
        const profileRole = roleKey(
          nextProfile.role || nextProfile.userRole || nextProfile.accountRole,
        );
        const statuses = [
          nextProfile.status,
          nextProfile.registrationStatus,
          nextProfile.roleApprovalStatus,
          nextProfile.approvalStatus,
          nextProfile.branchApprovalStatus,
          nextProfile.accountStatus,
        ]
          .map(upper)
          .filter(Boolean);
        const blocked =
          nextProfile.isActive === false ||
          isDeleted(nextProfile) ||
          statuses.some((value) =>
            ["PENDING", "REJECTED", "SUSPENDED", "BLOCKED", "INACTIVE", "DELETED"].includes(value),
          );
        const approved =
          statuses.includes("APPROVED") ||
          nextProfile.isApproved === true ||
          (nextProfile.isActive === true && !blocked);
        if (!allowed.includes(profileRole)) {
          setProfile(null);
          setError(
            `This account is ${human(profileRole)}, not an authorised Registrar.`,
          );
        } else if (!approved) {
          setProfile(null);
          setError(
            `Registrar account status is ${human(statuses[0] || "Pending")}.`,
          );
        } else if (!nextProfile.state) {
          setProfile(null);
          setError("Registrar profile State is missing. Add state or stateName in nafed_users.");
        } else if (allowed.some((role) => role.startsWith("DISTRICT")) && !nextProfile.district) {
          setProfile(null);
          setError("District Registrar jurisdiction is missing. Add district or districtName in nafed_users.");
        } else {
          setProfile({ ...nextProfile, role: profileRole });
          setError("");
        }
        setLoading(false);
      };

      try {
        let profileId = currentUser.uid;
        const direct = await getDoc(doc(db, COLLECTIONS.users, currentUser.uid));
        if (!direct.exists() && currentUser.email) {
          for (const field of ["email", "officialEmail", "loginEmail"]) {
            const result = await getDocs(
              query(
                collection(db, COLLECTIONS.users),
                where(field, "==", currentUser.email),
              ),
            );
            const match = result.docs.find((item) => {
              const data = item.data();
              return allowed.includes(
                roleKey(data.role || data.userRole || data.accountRole),
              );
            });
            if (match) {
              profileId = match.id;
              break;
            }
          }
        }
        if (!alive) return;
        stopProfile = onSnapshot(
          doc(db, COLLECTIONS.users, profileId),
          (snapshot) =>
            acceptProfile(
              snapshot.exists()
                ? { id: snapshot.id, ...snapshot.data() }
                : null,
            ),
          (snapshotError) => {
            setError(
              snapshotError.message || "Unable to load Registrar profile.",
            );
            setLoading(false);
          },
        );
      } catch (profileError) {
        setError(profileError.message || "Unable to load Registrar profile.");
        setLoading(false);
      }
    });
    return () => {
      alive = false;
      stopAuth();
      stopProfile();
    };
  }, [roles]);

  return { user, profile, loading, error };
}

export function useRegistrarData(profile, scope = "STATE") {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const state = recordState(profile);
    const district = recordDistrict(profile);
    if (!state || (scope === "DISTRICT" && !district)) {
      setProjects([]);
      setUsers([]);
      setActivities([]);
      setNotifications([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError("");
    let alive = true;
    let projectSourceReady = false;
    const sourceErrors = new Map();
    const sourceRows = new Map();
    const stateFields = [
      "state",
      "stateName",
      "projectState",
      "pacsState",
      "jurisdictionState",
      "applicationData.state",
      "applicationData.stateName",
      "formData.state",
      "formData.stateName",
      "projectDetails.state",
    ];
    const expectedProjectSources = 1;
    const setters = {
      [COLLECTIONS.projects]: setProjects,
      [COLLECTIONS.users]: setUsers,
      [COLLECTIONS.activity]: setActivities,
      [COLLECTIONS.notifications]: setNotifications,
    };
    const publish = (collectionName) => {
      const merged = new Map();
      [...sourceRows.entries()]
        .filter(([sourceKey]) => sourceKey.startsWith(`${collectionName}|`))
        .forEach(([, rows]) => rows.forEach((row) => merged.set(row.id, row)));
      setters[collectionName]([...merged.values()]);
    };
    const watch = (collectionName, field, value) => {
      const sourceKey = `${collectionName}|${field || "ALL"}|${value || ""}`;
      const source = field
        ? query(collection(db, collectionName), where(field, "==", value))
        : collection(db, collectionName);
      return onSnapshot(
        source,
        (snapshot) => {
          if (!alive) return;
          sourceRows.set(
            sourceKey,
            snapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
          );
          sourceErrors.delete(sourceKey);
          if (collectionName === COLLECTIONS.projects) {
            projectSourceReady = true;
            setLoading(false);
            setError("");
          }
          publish(collectionName);
        },
        (snapshotError) => {
          if (!alive) return;
          sourceErrors.set(sourceKey, snapshotError);
          const projectSources = [...sourceErrors.keys()].filter((key) =>
            key.startsWith(`${COLLECTIONS.projects}|`),
          ).length;
          if (!projectSourceReady && projectSources >= expectedProjectSources) {
            setLoading(false);
            setError(
              snapshotError.message ||
                "Registrar project data could not be loaded for this jurisdiction.",
            );
          }
        },
      );
    };
    const stops = [];
    // godown_projects is readable to signed-in portal users. Listen once and
    // scope locally with normalized aliases so "Gujarat", "GUJARAT" and old
    // nested applicationData fields all appear in the same Registrar register.
    stops.push(watch(COLLECTIONS.projects, "", ""));
    [COLLECTIONS.users, COLLECTIONS.activity, COLLECTIONS.notifications].forEach((collectionName) => {
      stateFields.forEach((field) =>
        stops.push(watch(collectionName, field, state)),
      );
    });
    return () => stops.forEach((stop) => stop());
  }, [profile, scope]);

  const scopedProjects = useMemo(() => {
    const state = normal(recordState(profile));
    const district = normal(recordDistrict(profile));
    return projects
      .filter((project) => !isDeleted(project) && isNafedProject(project))
      .filter((project) => normal(recordState(project)) === state)
      .filter(
        (project) =>
          scope !== "DISTRICT" ||
          normal(recordDistrict(project)) === district,
      )
      .sort(
        (a, b) =>
          timeOf(b.updatedAt || b.createdAt) -
          timeOf(a.updatedAt || a.createdAt),
      );
  }, [profile, projects, scope]);

  const scopedUsers = useMemo(() => {
    const state = normal(recordState(profile));
    const district = normal(recordDistrict(profile));
    return users.filter(
      (item) =>
        !isDeleted(item) &&
        normal(recordState(item)) === state &&
        (scope !== "DISTRICT" ||
          normal(recordDistrict(item)) === district),
    );
  }, [profile, scope, users]);

  const scopedActivity = useMemo(() => {
    const state = normal(recordState(profile));
    const district = normal(recordDistrict(profile));
    return activities
      .filter(
        (item) =>
          (!recordState(item) || normal(recordState(item)) === state) &&
          (scope !== "DISTRICT" ||
            !recordDistrict(item) ||
            normal(recordDistrict(item)) === district),
      )
      .sort(
        (a, b) =>
          timeOf(b.createdAt || b.updatedAt) -
          timeOf(a.createdAt || a.updatedAt),
      );
  }, [activities, profile, scope]);

  const scopedNotifications = useMemo(() => {
    const state = normal(recordState(profile));
    const district = normal(recordDistrict(profile));
    return notifications
      .filter(
        (item) =>
          (!recordState(item) || normal(recordState(item)) === state) &&
          (scope !== "DISTRICT" ||
            !recordDistrict(item) ||
            normal(recordDistrict(item)) === district),
      )
      .sort(
        (a, b) =>
          timeOf(b.createdAt || b.updatedAt) -
          timeOf(a.createdAt || a.updatedAt),
      );
  }, [notifications, profile, scope]);

  const news = useMemo(() => {
    const combined = [
      ...scopedActivity.map((item) => ({
        id: `activity-${item.id}`,
        type: human(item.action || item.type || "Activity"),
        text:
          item.details ||
          item.message ||
          `${item.pacsName || item.entityName || "Project"}: ${human(item.action)}`,
        at: item.createdAt || item.updatedAt,
      })),
      ...scopedNotifications.map((item) => ({
        id: `notification-${item.id}`,
        type: "Notification",
        text: item.message || item.title || human(item.type),
        at: item.createdAt || item.updatedAt,
      })),
    ].sort((a, b) => timeOf(b.at) - timeOf(a.at));
    const seen = new Set();
    return combined.filter((item) => {
      const minute = Math.floor(timeOf(item.at) / 60000);
      const key = `${normal(item.text)}|${minute}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [scopedActivity, scopedNotifications]);

  return {
    projects: scopedProjects,
    users: scopedUsers,
    activities: scopedActivity,
    notifications: scopedNotifications,
    news,
    loading,
    error,
  };
}

export async function runRegistrarAction({
  project,
  profile,
  action,
  message,
  patch,
  notificationType,
  targetRoles = [],
}) {
  if (!project?.id) throw new Error("Project record is unavailable.");
  if (!profile?.id) throw new Error("Registrar profile is unavailable.");
  const profileRole = upper(profile.role);
  const actorRole = profileRole.includes("DISTRICT")
    ? "DISTRICT_REGISTRAR"
    : profileRole.includes("STATE")
      ? "STATE_REGISTRAR"
      : profileRole;
  const projectRef = doc(db, COLLECTIONS.projects, project.id);
  const historyEntry = {
    action,
    message,
    byUid: profile.id,
    byName: profile.name || profile.fullName || profile.email || "Registrar",
    byRole: actorRole,
    atIso: new Date().toISOString(),
  };
  // Project state is authoritative. Save it first so a legacy audit or
  // notification permission cannot roll back an otherwise valid action.
  await updateDoc(projectRef, {
    ...patch,
    registrarWorkflowHistory: arrayUnion(historyEntry),
    lastAuthorityAction: action,
    lastAuthorityActionByUid: profile.id,
    lastAuthorityActionByRole: actorRole,
    lastAuthorityActionAt: serverTimestamp(),
    automationStatus: "SYNCHRONIZED",
    updatedAt: serverTimestamp(),
  });
  const activityRef = doc(collection(db, COLLECTIONS.activity));
  const activityPayload = {
    entityType: "GODOWN_PROJECT",
    entityId: project.id,
    projectId: project.id,
    applicationId: projectId(project),
    entityName: projectName(project),
    pacsName: projectName(project),
    state: project.state || profile.state,
    district: project.district || profile.district || "",
    action,
    details: message,
    performedByUid: profile.id,
    performedByName:
      profile.name || profile.fullName || profile.email || "Registrar",
    performedByRole: actorRole,
    createdAt: serverTimestamp(),
  };
  const notificationRef = doc(collection(db, COLLECTIONS.notifications));
  const notificationPayload = {
    type: notificationType || action,
    title: human(notificationType || action),
    message,
    projectId: project.id,
    applicationId: projectId(project),
    pacsName: projectName(project),
    state: project.state || profile.state,
    district: project.district || profile.district || "",
    targetRoles,
    createdByUid: profile.id,
    createdByRole: actorRole,
    isRead: false,
    createdAt: serverTimestamp(),
  };
  const secondaryWrites = await Promise.allSettled([
    setDoc(activityRef, activityPayload),
    setDoc(notificationRef, notificationPayload),
  ]);
  secondaryWrites.forEach((result, index) => {
    if (result.status === "rejected") {
      console.error(
        index === 0
          ? "Registrar activity audit write failed"
          : "Registrar notification write failed",
        result.reason,
      );
    }
  });
  return historyEntry;
}

export async function updateRegistrarUser({
  registrar,
  profile,
  status,
  remarks,
}) {
  if (!registrar?.id)
    throw new Error("District Registrar record is unavailable.");
  const batch = writeBatch(db);
  batch.update(doc(db, COLLECTIONS.users, registrar.id), {
    status,
    registrationStatus: status,
    isActive: status === "APPROVED",
    stateRegistrarRemarks: remarks || "",
    approvedByUid: status === "APPROVED" ? profile.id : null,
    approvedByRole: "STATE_REGISTRAR",
    approvedAt: status === "APPROVED" ? serverTimestamp() : null,
    updatedAt: serverTimestamp(),
  });
  const activityRef = doc(collection(db, COLLECTIONS.activity));
  batch.set(activityRef, {
    entityType: "NAFED_USER",
    entityId: registrar.id,
    entityName: registrar.name || registrar.fullName || registrar.email,
    state: registrar.state || profile.state,
    district: registrar.district || "",
    action: `STATE_REGISTRAR_DISTRICT_REGISTRAR_${status}`,
    details: remarks || `${human(status)} by State Registrar`,
    performedByUid: profile.id,
    performedByName: profile.name || profile.fullName || profile.email,
    performedByRole: "STATE_REGISTRAR",
    createdAt: serverTimestamp(),
  });
  await batch.commit();
}

export async function uploadOfficialFile({
  file,
  role,
  uid,
  projectId: linkedProjectId,
  folder,
  maxMb = 15,
}) {
  if (!file) throw new Error("Select a PDF file.");
  if (file.type !== "application/pdf")
    throw new Error("Only PDF files are accepted.");
  if (file.size <= 0 || file.size > maxMb * 1024 * 1024)
    throw new Error(`PDF must be smaller than ${maxMb} MB.`);
  const safeName = String(file.name || "official.pdf")
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .slice(-100);
  const root = upper(role).includes("STATE")
    ? "nafed_state_registrar"
    : "nafed_district";
  const path = `${root}/${uid}/${linkedProjectId}/${folder}/${Date.now()}_${safeName}`;
  const reference = storageRef(storage, path);
  await uploadBytes(reference, file, { contentType: "application/pdf" });
  const url = await getDownloadURL(reference);
  return { url, path, name: safeName, size: file.size };
}

export function downloadCsv(fileName, rows) {
  const csv = rows
    .map((row) =>
      row
        .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
        .join(","),
    )
    .join("\n");
  const url = URL.createObjectURL(
    new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName.replaceAll("/", "-");
  anchor.click();
  URL.revokeObjectURL(url);
}

export function StatusPill({ value }) {
  const status = upper(value);
  const tone = /APPROV|COMPLETE|GRANT|CLEAR|FORWARD|READY|AVAILABLE|RECEIVED/.test(status)
    ? "success"
    : /REJECT|ADVERSE|CANCEL/.test(status)
      ? "danger"
      : /QUERY|PENDING|WAIT|SCHEDULE/.test(status)
        ? "warning"
        : "info";
  return (
    <span className={`rg-status ${tone}`}>{human(value || "Pending")}</span>
  );
}

export function WorkflowBar({ project }) {
  const active = workflowIndex(project);
  return (
    <div className="rg-workflow">
      {WORKFLOW.map((label, index) => (
        <div
          className={index < active ? "done" : index === active ? "active" : ""}
          key={label}
        >
          <i>{index < active ? "✓" : index + 1}</i>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

export function PageTitle({ eyebrow, title, description, actions }) {
  return (
    <header className="rg-page-title">
      <div>
        <small>{eyebrow}</small>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions && <aside>{actions}</aside>}
    </header>
  );
}

export function Metric({ label, value, note, tone = "blue" }) {
  return (
    <article className={`rg-metric ${tone}`}>
      <span>{label}</span>
      <b>{value}</b>
      <small>{note}</small>
    </article>
  );
}

export function DocumentControl({ project }) {
  const documents = registrarDocumentMatrix(project);
  const readiness = documentReadiness(project);
  return (
    <section className="rg-panel rg-document-control">
      <div className="rg-panel-head">
        <div>
          <small>UNIFIED AUTHORITY DOCUMENT CONTROL</small>
          <h3>Official Project Document Matrix</h3>
          <p>
            Live files from PACS, PMC, District and State are shown without
            creating duplicate records.
          </p>
        </div>
        <aside>
          <StatusPill
            value={`${readiness.complete}/${readiness.total} mandatory ready`}
          />
        </aside>
      </div>
      <div className="rg-document-grid">
        {documents.map((item, index) => (
          <article className={item.document ? "ready" : "pending"} key={item.key}>
            <i>{item.document ? "✓" : String(index + 1).padStart(2, "0")}</i>
            <div>
              <b>{item.label}</b>
              <span>{item.owner} · {item.required ? "Mandatory" : "Workflow document"}</span>
            </div>
            {item.document ? (
              <a href={item.document.url} target="_blank" rel="noreferrer">
                View / Download
              </a>
            ) : (
              <em>Not available yet</em>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

export function AutomationQueue({ projects = [], scope, onOpen, limit = 8 }) {
  const rows = projects
    .map((project) => ({ project, ...registrarAutomation(project, scope) }))
    .filter((item) => item.actionable || /Compliance/.test(item.queue))
    .sort((left, right) => {
      const rank = { CRITICAL: 4, HIGH: 3, DUE: 2, NORMAL: 1 };
      return (
        (rank[right.priority] || 0) - (rank[left.priority] || 0) ||
        right.ageDays - left.ageDays
      );
    })
    .slice(0, limit);
  return (
    <section className="rg-panel rg-automation">
      <div className="rg-panel-head">
        <div>
          <small>AUTOMATED NEXT-ACTION ENGINE</small>
          <h3>Priority Authority Queue</h3>
          <p>Live stage, pending action and ageing are calculated automatically.</p>
        </div>
        <StatusPill value={`${rows.length} priority files`} />
      </div>
      {rows.length ? (
        <div className="rg-automation-list">
          {rows.map((item) => (
            <article key={item.project.id}>
              <span className={`rg-priority ${normal(item.priority)}`}>
                {human(item.priority)}
              </span>
              <div>
                <b>{projectName(item.project)}</b>
                <small>{projectId(item.project)} · {item.queue}</small>
              </div>
              <p>{item.nextAction}</p>
              <time>{item.ageDays} day ageing</time>
              <button className="rg-link" onClick={() => onOpen(item.project, item.actionKey)}>
                Open File →
              </button>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState title="All authority queues are current" text="No actionable file is overdue or waiting in this jurisdiction." />
      )}
    </section>
  );
}

export function EmptyState({ title = "No record found", text }) {
  return (
    <div className="rg-empty">
      <i>✓</i>
      <h3>{title}</h3>
      <p>{text || "No file requires action in this stage."}</p>
    </div>
  );
}

export function ActivityList({ items = [], limit }) {
  const visible = limit ? items.slice(0, limit) : items;
  return (
    <div className="rg-activity-list">
      {visible.map((item) => (
        <article key={item.id}>
          <i>{String(item.type || "A").slice(0, 1)}</i>
          <div>
            <b>{item.type}</b>
            <p>{item.text}</p>
          </div>
          <time>{dateTime(item.at)}</time>
        </article>
      ))}
      {!visible.length && (
        <EmptyState
          title="No live activity"
          text="New authority actions will appear here."
        />
      )}
    </div>
  );
}

export function PortalShell({
  authority,
  subtitle,
  profile,
  navigation,
  active,
  onNavigate,
  news,
  children,
}) {
  const now = useClock();
  const logout = async () => {
    await signOut(auth);
    window.location.href = "/nafed";
  };
  const activeModule =
    navigation.find((item) => item.key === active) || navigation[0] || {};
  const initials = String(profile?.name || profile?.fullName || "Registrar")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  return (
    <div className={`rg-app ${active === "overview" ? "rg-home" : "rg-module-page"}`}>
      <style>{PORTAL_CSS}</style>
      <div className="rg-tricolor" />
      <header className="rg-topbar">
        <div className="rg-brand">
          <img src="/images/nafed-logo.png" alt="NAFED" />
          <div>
            <b>{authority}</b>
            <span>{subtitle}</span>
          </div>
        </div>
        <div className="rg-session">
          <strong className="rg-live"><i /> Live Sync</strong>
          <time>
            <b>
              {now.toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </b>
            <span>{dateOnly(now)}</span>
          </time>
          <i>{initials || "RG"}</i>
          <div>
            <b>{profile?.name || profile?.fullName || "Registrar"}</b>
            <span>
              {profile?.district || profile?.state || human(profile?.role)}
            </span>
          </div>
          <button onClick={logout}>Logout</button>
        </div>
      </header>
      <div className="rg-body">
        {active === "overview" ? (
          <section className="rg-launcher">
            <div className="rg-launcher-heading">
              <img src="/images/nafed-logo.png" alt="NAFED" />
              <small>WORLD&apos;S LARGEST GRAIN STORAGE PLAN</small>
              <h1>{authority} Digital Workspace</h1>
              <p>
                {profile?.district || profile?.state || "Approved jurisdiction"}
                {" · "}Secure authority access
              </p>
            </div>
            <div className="rg-launcher-summary">
              <span>Authority<b>{human(profile?.role || authority)}</b></span>
              <span>Jurisdiction<b>{profile?.district || profile?.state || "-"}</b></span>
              <span>Live Records<b>Firestore connected</b></span>
              <span>Audit Trail<b>Permanent &amp; automated</b></span>
            </div>
            <nav className="rg-module-grid" aria-label="Registrar modules">
              {navigation.map((item, index) => (
                <button
                  className={active === item.key ? "active" : ""}
                  key={item.key}
                  onClick={() => onNavigate(item.key)}
                >
                  <i>{String(index + 1).padStart(2, "0")}</i>
                  <div>
                    <b>{item.label}</b>
                    <span>
                      {item.key === "overview"
                        ? "Live authority dashboard and workflow summary"
                        : "Open official module and verified records"}
                    </span>
                  </div>
                  <footer>
                    <em>Open Module</em>
                    {item.count !== undefined && <strong>{item.count}</strong>}
                  </footer>
                </button>
              ))}
            </nav>
          </section>
        ) : (
          <section className="rg-module-bar">
            <button onClick={() => onNavigate("overview")}>← Back to Dashboard</button>
            <div>
              <small>AUTHORISED REGISTRAR MODULE</small>
              <h1>{activeModule.label || authority}</h1>
              <p>{profile?.district || profile?.state || "Approved jurisdiction"}</p>
            </div>
            {activeModule.count !== undefined && (
              <strong>{activeModule.count} LIVE RECORDS</strong>
            )}
          </section>
        )}
        <div className="rg-ticker">
          <b>LIVE UPDATE</b>
          <span>
            {news?.[0]?.text ||
              "Registrar control centre is connected to live portal records."}
          </span>
          <time>{news?.[0] ? dateTime(news[0].at) : "Live"}</time>
        </div>
        <main className="rg-main">{children}</main>
      </div>
    </div>
  );
}

export function LoadingScreen({
  title = "Loading Registrar Control Centre",
  error,
}) {
  return (
    <div className="rg-loading">
      <style>{PORTAL_CSS}</style>
      <div className="rg-tricolor" />
      <img src="/images/nafed-logo.png" alt="NAFED" />
      <h1>{error ? "Access unavailable" : title}</h1>
      <p>{error || "Connecting to live NAFED records..."}</p>
      {error && <button onClick={() => window.location.reload()}>Retry</button>}
    </div>
  );
}

export const PORTAL_CSS = `
  :root{--rg-navy:#092f5b;--rg-navy-2:#123f70;--rg-blue:#1769b0;--rg-green:#19764a;--rg-orange:#dc8a16;--rg-purple:#6848a8;--rg-red:#b33b35;--rg-bg:#edf2f7;--rg-line:#d5e0eb;--rg-ink:#102943;--rg-muted:#60748a;--rg-white:#fff;--rg-shadow:0 12px 30px rgba(12,44,75,.075)}
  *{box-sizing:border-box}.rg-app{min-height:100vh;background:var(--rg-bg);color:var(--rg-ink);font-family:Inter,"Segoe UI",Arial,sans-serif}.rg-tricolor{height:4px;background:linear-gradient(90deg,#e5962e 0 33%,#f8fafc 33% 66%,#278453 66%)}
  .rg-topbar{height:92px;background:#fff;display:flex;align-items:center;justify-content:space-between;padding:0 32px;border-bottom:1px solid var(--rg-line)}.rg-brand{display:flex;align-items:center;gap:15px}.rg-brand img{width:64px;height:64px;object-fit:contain}.rg-brand div{display:flex;flex-direction:column}.rg-brand b{font-family:Georgia,serif;font-size:24px;color:var(--rg-navy)}.rg-brand span{font-size:12px;color:var(--rg-muted);font-weight:700;margin-top:5px}.rg-session{display:flex;align-items:center;gap:12px}.rg-session time,.rg-session>div{display:flex;flex-direction:column}.rg-session time{align-items:flex-end;margin-right:8px}.rg-session time b{font-size:14px}.rg-session span{font-size:11px;color:var(--rg-muted);margin-top:3px}.rg-session>i{width:40px;height:40px;border-radius:50%;display:grid;place-items:center;background:#e5f3e9;color:var(--rg-green);font-style:normal;font-weight:900;border:1px solid #b9d9c4}.rg-session>div b{font-size:13px}.rg-session button,.rg-btn{border:0;border-radius:8px;background:var(--rg-navy);color:#fff;padding:11px 16px;font-weight:800;cursor:pointer}.rg-session button{margin-left:10px;background:#fff;color:var(--rg-navy);border:1px solid #bdcad8}.rg-btn:hover,.rg-session button:hover{filter:brightness(.96)}.rg-btn:disabled{opacity:.45;cursor:not-allowed}.rg-btn.secondary{background:#fff;color:var(--rg-navy);border:1px solid #aebfd1}.rg-btn.green{background:var(--rg-green)}.rg-btn.orange{background:var(--rg-orange)}.rg-btn.red{background:var(--rg-red)}
  .rg-ticker{height:46px;background:var(--rg-navy);color:#fff;display:flex;align-items:center;gap:16px;padding:0 32px}.rg-ticker b{height:46px;display:flex;align-items:center;background:var(--rg-green);padding:0 18px;margin-left:-32px;font-size:11px;letter-spacing:.8px}.rg-ticker span{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:13px}.rg-ticker time{font-size:11px;color:#d7e4f0}
  .rg-body{max-width:1660px;margin:0 auto;display:grid;grid-template-columns:248px minmax(0,1fr);min-height:calc(100vh - 142px)}.rg-sidebar{background:#fff;border-right:1px solid var(--rg-line);padding:24px 15px;display:flex;flex-direction:column}.rg-sidebar>small{font-size:10px;color:var(--rg-green);font-weight:900;letter-spacing:1px;padding:0 10px 12px}.rg-sidebar nav{display:flex;flex-direction:column;gap:6px}.rg-sidebar nav button{border:0;background:transparent;border-radius:10px;display:grid;grid-template-columns:32px 1fr auto;align-items:center;gap:9px;text-align:left;padding:12px 10px;color:#425a72;cursor:pointer;font-weight:700}.rg-sidebar nav button:hover{background:#f0f5fa}.rg-sidebar nav button.active{background:#e9f1fb;color:var(--rg-navy);box-shadow:inset 3px 0 var(--rg-blue)}.rg-sidebar nav button i{font-style:normal;font-size:10px;color:#7d91a6}.rg-sidebar nav button span{font-size:13px}.rg-sidebar nav button b{min-width:24px;height:24px;padding:0 6px;border-radius:12px;display:grid;place-items:center;background:#e7f3ea;color:var(--rg-green);font-size:11px}.rg-sidebar footer{margin-top:auto;border:1px solid #cfe4d5;background:#f2faf4;border-radius:12px;padding:15px;display:flex;flex-direction:column}.rg-sidebar footer span,.rg-sidebar footer small{font-size:10px;color:var(--rg-muted)}.rg-sidebar footer b{margin:5px 0;color:var(--rg-green)}
  .rg-main{padding:28px;min-width:0}.rg-page-title{display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:22px}.rg-page-title small{font-size:10px;letter-spacing:1.2px;color:var(--rg-green);font-weight:900}.rg-page-title h1{font-family:Georgia,serif;font-size:29px;color:var(--rg-navy);margin:7px 0 6px}.rg-page-title p{margin:0;color:var(--rg-muted);font-size:13px;max-width:760px;line-height:1.55}.rg-page-title aside{display:flex;gap:9px;flex-wrap:wrap;justify-content:flex-end}
  .rg-hero{background:linear-gradient(120deg,#0b2e59,#154f83);color:#fff;border-radius:18px;padding:25px 28px;margin-bottom:20px;display:grid;grid-template-columns:minmax(0,1fr) auto;gap:24px;align-items:center;box-shadow:0 14px 32px rgba(11,46,89,.15)}.rg-hero small{font-size:10px;letter-spacing:1.4px;color:#aee0bc;font-weight:900}.rg-hero h2{font-family:Georgia,serif;font-size:28px;margin:8px 0}.rg-hero p{max-width:720px;margin:0;color:#d5e3f0;font-size:13px;line-height:1.6}.rg-hero aside{display:flex;gap:16px}.rg-hero aside span{display:flex;flex-direction:column;min-width:120px;border-left:1px solid rgba(255,255,255,.25);padding-left:16px}.rg-hero aside b{font-size:24px}.rg-hero aside small{color:#d5e3f0;letter-spacing:0;margin-top:4px}
  .rg-metrics{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:14px;margin-bottom:20px}.rg-metric{background:#fff;border:1px solid var(--rg-line);border-top:4px solid var(--rg-blue);border-radius:13px;padding:17px;display:flex;flex-direction:column;min-height:112px;box-shadow:0 5px 15px rgba(22,54,87,.04)}.rg-metric.green{border-top-color:var(--rg-green)}.rg-metric.orange{border-top-color:var(--rg-orange)}.rg-metric.purple{border-top-color:var(--rg-purple)}.rg-metric.red{border-top-color:var(--rg-red)}.rg-metric span{font-size:11px;color:var(--rg-muted);font-weight:800}.rg-metric b{font-family:Georgia,serif;font-size:26px;color:var(--rg-navy);margin:9px 0 5px}.rg-metric small{font-size:10px;color:#8191a1}
  .rg-grid-2{display:grid;grid-template-columns:minmax(0,1.55fr) minmax(300px,.75fr);gap:18px}.rg-panel{background:#fff;border:1px solid var(--rg-line);border-radius:15px;padding:20px;box-shadow:0 7px 20px rgba(22,54,87,.045);margin-bottom:18px}.rg-panel-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:16px}.rg-panel-head small{font-size:10px;color:var(--rg-green);font-weight:900;letter-spacing:1px}.rg-panel-head h2,.rg-panel-head h3{font-family:Georgia,serif;color:var(--rg-navy);margin:5px 0 3px}.rg-panel-head h2{font-size:22px}.rg-panel-head h3{font-size:19px}.rg-panel-head p{margin:0;color:var(--rg-muted);font-size:12px;line-height:1.5}.rg-panel-head aside{display:flex;gap:8px;align-items:center;flex-wrap:wrap;justify-content:flex-end}
  .rg-filters{display:grid;grid-template-columns:minmax(220px,1fr) 200px 200px auto;gap:10px;margin-bottom:15px}.rg-input,.rg-select,.rg-textarea{width:100%;border:1px solid #bdcad8;border-radius:8px;background:#fff;color:var(--rg-ink);padding:11px 12px;font:inherit;font-size:12px}.rg-textarea{min-height:90px;resize:vertical}.rg-input:focus,.rg-select:focus,.rg-textarea:focus{outline:3px solid rgba(24,93,171,.12);border-color:var(--rg-blue)}.rg-field{display:flex;flex-direction:column;gap:6px}.rg-field label{font-size:11px;font-weight:800;color:#496178}.rg-form-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:13px}.rg-form-grid .wide{grid-column:1/-1}.rg-actions{display:flex;gap:9px;justify-content:flex-end;flex-wrap:wrap;margin-top:15px}
  .rg-table-wrap{overflow:auto;border:1px solid var(--rg-line);border-radius:11px}.rg-table{width:100%;border-collapse:collapse;min-width:920px}.rg-table th{background:#f4f7fa;color:#597086;text-transform:uppercase;letter-spacing:.4px;font-size:9px;text-align:left;padding:11px;border-bottom:1px solid var(--rg-line)}.rg-table td{padding:12px 11px;border-bottom:1px solid #e5ebf1;font-size:12px;vertical-align:middle}.rg-table tr:last-child td{border-bottom:0}.rg-table tr:hover td{background:#fbfdff}.rg-table td>small,.rg-table td>b{display:block}.rg-table td>b{color:var(--rg-navy);font-size:12px}.rg-table td>small{color:var(--rg-muted);font-size:10px;margin-top:3px}.rg-link{border:0;background:transparent;color:var(--rg-blue);font-weight:900;cursor:pointer;padding:4px}.rg-link:hover{text-decoration:underline}
  .rg-status{display:inline-flex;align-items:center;white-space:nowrap;border-radius:14px;padding:6px 9px;font-size:9px;font-weight:900;text-transform:uppercase;letter-spacing:.35px;background:#e9f1fb;color:var(--rg-blue)}.rg-status.success{background:#e6f5eb;color:#217348}.rg-status.warning{background:#fff2d9;color:#9a6008}.rg-status.danger{background:#fde9e7;color:#a6322d}.rg-status.info{background:#ede9fa;color:#6244a5}
  .rg-workflow{display:grid;grid-template-columns:repeat(6,1fr);margin:12px 0 18px}.rg-workflow>div{position:relative;text-align:center;color:#8b9baa;font-size:10px;font-weight:700}.rg-workflow>div:before{content:"";position:absolute;top:14px;left:-50%;width:100%;height:2px;background:#dce5ee}.rg-workflow>div:first-child:before{display:none}.rg-workflow i{position:relative;z-index:1;width:29px;height:29px;border-radius:50%;display:grid;place-items:center;margin:0 auto 7px;background:#e7edf3;color:#708397;font-style:normal;font-size:10px}.rg-workflow .done:before,.rg-workflow .active:before{background:var(--rg-green)}.rg-workflow .done i{background:var(--rg-green);color:#fff}.rg-workflow .active i{background:var(--rg-blue);color:#fff;box-shadow:0 0 0 5px #dbe9fa}.rg-workflow .active span{color:var(--rg-blue)}
  .rg-activity-list{display:flex;flex-direction:column}.rg-activity-list>article{display:grid;grid-template-columns:34px minmax(0,1fr) auto;gap:11px;align-items:center;padding:12px 0;border-bottom:1px solid #e4ebf2}.rg-activity-list>article:last-child{border-bottom:0}.rg-activity-list>article>i{width:30px;height:30px;border-radius:8px;display:grid;place-items:center;background:#e8f1fb;color:var(--rg-blue);font-style:normal;font-weight:900}.rg-activity-list article b{font-size:11px;color:var(--rg-green)}.rg-activity-list article p{margin:3px 0 0;font-size:11px;color:#536a81;line-height:1.4}.rg-activity-list time{font-size:9px;color:#8796a5;text-align:right}
  .rg-empty{padding:36px 16px;text-align:center}.rg-empty i{width:42px;height:42px;margin:auto;border-radius:50%;display:grid;place-items:center;background:#e7f5eb;color:var(--rg-green);font-style:normal;font-weight:900}.rg-empty h3{font-family:Georgia,serif;color:var(--rg-navy);margin:12px 0 5px}.rg-empty p{color:var(--rg-muted);font-size:12px;margin:0}.rg-alert{padding:12px 14px;border-radius:9px;background:#e9f2fb;border:1px solid #bdd2e8;color:#174e83;font-size:12px;margin-bottom:14px}.rg-alert.success{background:#e9f6ed;border-color:#b7ddc2;color:#246e43}.rg-alert.error{background:#fcebea;border-color:#efc0bd;color:#9c302b}
  .rg-drawer-backdrop{position:fixed;inset:0;background:rgba(8,27,48,.45);z-index:50;display:flex;justify-content:flex-end}.rg-drawer{height:100%;width:min(820px,94vw);background:#f3f6f9;overflow:auto;box-shadow:-20px 0 50px rgba(8,27,48,.2)}.rg-drawer-head{position:sticky;top:0;z-index:2;background:#fff;border-bottom:1px solid var(--rg-line);padding:18px 22px;display:flex;justify-content:space-between;gap:14px}.rg-drawer-head h2{font-family:Georgia,serif;color:var(--rg-navy);margin:3px 0}.rg-drawer-head p{margin:0;color:var(--rg-muted);font-size:11px}.rg-drawer-head button{width:36px;height:36px;border-radius:50%;border:1px solid var(--rg-line);background:#fff;cursor:pointer}.rg-drawer-body{padding:20px}.rg-summary{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}.rg-summary>div{background:#f6f9fb;border:1px solid var(--rg-line);border-radius:9px;padding:11px;display:flex;flex-direction:column}.rg-summary span{font-size:9px;color:var(--rg-muted);text-transform:uppercase}.rg-summary b{font-size:12px;color:var(--rg-navy);margin-top:5px}.rg-doc-list{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:9px}.rg-doc-list a{display:flex;flex-direction:column;border:1px solid var(--rg-line);border-radius:9px;padding:11px;text-decoration:none;color:var(--rg-navy);background:#fff}.rg-doc-list a:hover{border-color:var(--rg-blue)}.rg-doc-list a b{font-size:11px}.rg-doc-list a span{font-size:9px;color:var(--rg-green);margin-top:4px}.rg-checklist{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:9px}.rg-checklist label{display:flex;gap:9px;border:1px solid var(--rg-line);background:#fff;border-radius:9px;padding:11px;font-size:11px;line-height:1.35}.rg-checklist input{margin-top:2px}.rg-modal-backdrop{position:fixed;inset:0;background:rgba(8,27,48,.5);z-index:70;display:grid;place-items:center;padding:20px}.rg-modal{background:#fff;border-radius:15px;width:min(620px,96vw);max-height:90vh;overflow:auto;box-shadow:0 25px 65px rgba(8,27,48,.25)}.rg-modal header{padding:19px 21px;border-bottom:1px solid var(--rg-line);display:flex;justify-content:space-between}.rg-modal header h2{font-family:Georgia,serif;color:var(--rg-navy);font-size:20px;margin:0}.rg-modal header button{border:0;background:transparent;font-size:20px;cursor:pointer}.rg-modal section{padding:20px}
  .rg-loading{min-height:100vh;background:#eef3f8;display:flex;align-items:center;justify-content:center;flex-direction:column;text-align:center;color:var(--rg-navy);font-family:Inter,"Segoe UI",Arial,sans-serif}.rg-loading .rg-tricolor{position:fixed;top:0;left:0;right:0}.rg-loading img{width:92px;height:92px;object-fit:contain;background:#fff;border-radius:18px;padding:8px;box-shadow:0 10px 30px rgba(11,46,89,.12)}.rg-loading h1{font-family:Georgia,serif;margin:18px 0 7px}.rg-loading p{color:var(--rg-muted);max-width:520px}.rg-loading button{margin-top:12px;border:0;border-radius:8px;background:var(--rg-navy);color:#fff;padding:11px 20px;font-weight:800;cursor:pointer}
  .rg-govbar{height:31px;padding:0 32px;background:#fff;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid #e4eaf0;color:#465d73;font-size:10px;font-weight:750;letter-spacing:.18px}.rg-topbar{height:88px;box-shadow:0 5px 18px rgba(11,46,89,.045);position:relative;z-index:3}.rg-brand b{font-family:Inter,"Segoe UI",Arial,sans-serif;font-size:23px;font-weight:900;letter-spacing:-.35px}.rg-brand span{letter-spacing:.15px}.rg-live{display:inline-flex;align-items:center;gap:7px;padding:7px 10px;border:1px solid #bfe0ca;background:#eef9f2;color:var(--rg-green);border-radius:999px;font-size:9px;letter-spacing:.5px}.rg-live i{width:7px;height:7px;border-radius:50%;background:#28a362;box-shadow:0 0 0 4px rgba(40,163,98,.12)}
  .rg-body{max-width:1800px;grid-template-columns:262px minmax(0,1fr);min-height:calc(100vh - 169px)}.rg-sidebar{background:linear-gradient(180deg,#0a315d,#0a284a);border-right:0;padding:26px 15px;box-shadow:8px 0 28px rgba(8,34,62,.08)}.rg-sidebar>small{color:#9fd9b3}.rg-sidebar nav{gap:7px}.rg-sidebar nav button{color:#d7e3ee;padding:12px 11px;border:1px solid transparent}.rg-sidebar nav button:hover{background:rgba(255,255,255,.07);color:#fff}.rg-sidebar nav button.active{background:#fff;color:var(--rg-navy);box-shadow:0 9px 22px rgba(1,18,35,.22);border-color:rgba(255,255,255,.75)}.rg-sidebar nav button i{color:#8eabc4}.rg-sidebar nav button.active i{color:var(--rg-green)}.rg-sidebar nav button b{background:#dff3e6;color:#176b43}.rg-sidebar footer{border-color:rgba(255,255,255,.17);background:rgba(255,255,255,.07)}.rg-sidebar footer span,.rg-sidebar footer small{color:#a9bdcf}.rg-sidebar footer b{color:#fff}.rg-main{padding:30px 32px 44px;background:radial-gradient(circle at 95% 0,#e3f1e8 0,transparent 290px),var(--rg-bg)}
  .rg-page-title{border-bottom:1px solid #d7e1eb;padding-bottom:18px}.rg-page-title h1{font-family:Inter,"Segoe UI",Arial,sans-serif;font-size:30px;font-weight:850;letter-spacing:-.55px}.rg-hero{position:relative;overflow:hidden;background:linear-gradient(118deg,#092f5b 0%,#124f75 56%,#176443 100%);border:1px solid rgba(255,255,255,.22);border-radius:16px;padding:29px 31px;box-shadow:0 18px 40px rgba(9,47,91,.18)}.rg-hero:after{content:"NAFED";position:absolute;right:24px;bottom:-25px;color:rgba(255,255,255,.045);font-size:94px;font-weight:950;letter-spacing:-7px}.rg-hero h2{font-family:Inter,"Segoe UI",Arial,sans-serif;font-weight:850;font-size:27px;letter-spacing:-.4px}.rg-hero aside{position:relative;z-index:1}.rg-metric{position:relative;overflow:hidden;border-top-width:3px;border-radius:12px;padding:18px;min-height:116px;box-shadow:var(--rg-shadow)}.rg-metric:after{content:"";position:absolute;width:54px;height:54px;border-radius:50%;right:-22px;bottom:-24px;background:rgba(24,93,171,.08)}.rg-metric.green:after{background:rgba(25,118,74,.09)}.rg-metric.orange:after{background:rgba(220,138,22,.1)}.rg-metric.purple:after{background:rgba(104,72,168,.1)}.rg-metric b{font-family:Inter,"Segoe UI",Arial,sans-serif;font-weight:900;letter-spacing:-.4px}.rg-panel{border-radius:13px;box-shadow:var(--rg-shadow)}.rg-panel-head h2,.rg-panel-head h3{font-family:Inter,"Segoe UI",Arial,sans-serif;font-weight:850;letter-spacing:-.25px}.rg-btn{min-height:40px;border-radius:7px;box-shadow:0 5px 13px rgba(9,47,91,.12)}.rg-btn.secondary{box-shadow:none}.rg-table th{background:#edf3f8;color:#4b657c;padding:12px}.rg-table td{padding:13px 12px}.rg-table tbody tr:nth-child(even) td{background:#fbfcfe}.rg-table tbody tr:hover td{background:#f1f7fb}.rg-drawer{width:min(960px,96vw);background:#edf2f7}.rg-drawer-head{border-top:4px solid var(--rg-green);box-shadow:0 5px 18px rgba(10,42,74,.08)}
  .rg-document-control{border-top:4px solid var(--rg-blue)}.rg-document-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.rg-document-grid article{display:grid;grid-template-columns:34px minmax(0,1fr) auto;align-items:center;gap:10px;padding:12px;border:1px solid var(--rg-line);border-radius:9px;background:#fff}.rg-document-grid article.ready{border-left:4px solid var(--rg-green)}.rg-document-grid article.pending{border-left:4px solid #c7d2dc;background:#f8fafc}.rg-document-grid article>i{width:29px;height:29px;border-radius:8px;display:grid;place-items:center;background:#e7f5ec;color:var(--rg-green);font-style:normal;font-size:10px;font-weight:900}.rg-document-grid article.pending>i{background:#e9eef3;color:#73869a}.rg-document-grid article div{display:flex;min-width:0;flex-direction:column}.rg-document-grid article b{font-size:11px;color:var(--rg-navy)}.rg-document-grid article span{margin-top:4px;font-size:9px;color:var(--rg-muted)}.rg-document-grid article a{border:1px solid #aabed0;border-radius:6px;padding:7px 8px;color:var(--rg-blue);text-decoration:none;font-size:9px;font-weight:900;white-space:nowrap}.rg-document-grid article em{font-size:9px;color:#8b99a8;white-space:nowrap}
  .rg-automation{border-top:4px solid var(--rg-green)}.rg-automation-list{display:flex;flex-direction:column}.rg-automation-list article{display:grid;grid-template-columns:76px minmax(190px,1.15fr) minmax(220px,1.4fr) 86px auto;align-items:center;gap:13px;padding:12px 2px;border-bottom:1px solid #e2e9f0}.rg-automation-list article:last-child{border-bottom:0}.rg-automation-list article>div{display:flex;flex-direction:column}.rg-automation-list article b{font-size:11px;color:var(--rg-navy)}.rg-automation-list article small{font-size:9px;color:var(--rg-muted);margin-top:4px}.rg-automation-list article p{font-size:11px;color:#425c73;margin:0}.rg-automation-list article time{font-size:9px;color:#7c8fa1}.rg-priority{display:inline-flex;justify-content:center;padding:6px 8px;border-radius:999px;background:#edf2f6;color:#536b80;font-size:8px;font-weight:950;letter-spacing:.4px}.rg-priority.due{background:#e7f0fb;color:#1c5a98}.rg-priority.high{background:#fff0d2;color:#945d08}.rg-priority.critical{background:#fde7e5;color:#a02f29}
  /* PACS-style registrar shell: full width, no sidebar. */
  .rg-app{background:#eef2f5}.rg-topbar{height:84px;padding:7px max(28px,calc((100vw - 1420px)/2));border-bottom:1px solid #b8c4d0;box-shadow:none}.rg-brand{gap:13px}.rg-brand img{width:70px;height:68px;padding:2px;mix-blend-mode:multiply}.rg-brand b{font-size:21px;color:#12345b}.rg-brand span{font-size:11px;color:#526273}.rg-session>i{width:43px;height:43px}.rg-session button{border-radius:5px;padding:10px 14px}.rg-body{display:block;max-width:1420px;margin:0 auto;padding:22px 0 36px;min-height:calc(100vh - 88px)}.rg-main{padding:18px 0 0;background:transparent}.rg-ticker{height:42px;border-radius:6px;overflow:hidden;padding:0 18px;margin-top:13px;box-shadow:none}.rg-ticker b{height:42px;margin-left:-18px;padding:0 15px}.rg-launcher{background:#fff;border:1px solid #b9c5d0;border-top:4px solid var(--rg-green);border-radius:7px;padding:17px 20px 18px}.rg-launcher-heading{display:grid;grid-template-columns:80px 1fr;grid-template-rows:auto auto auto;column-gap:16px;align-items:center}.rg-launcher-heading>img{grid-row:1/4;width:78px;height:82px;object-fit:contain;border:1px solid #cbd5df;border-radius:5px;padding:4px}.rg-launcher-heading small{color:var(--rg-green);font-size:10px;font-weight:900;letter-spacing:1.1px}.rg-launcher-heading h1{margin:3px 0;color:#12345b;font-size:26px;line-height:1.15}.rg-launcher-heading p{margin:0;color:#4d5d6c;font-size:12px;font-weight:650}.rg-launcher-summary{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));margin:15px 0 13px;border:1px solid #b9c5d0;border-radius:6px;overflow:hidden;background:#fff}.rg-launcher-summary span{padding:12px 15px;border-right:1px solid #cbd4dd;color:#5f6d7a;font-size:9px;font-weight:700;text-transform:uppercase}.rg-launcher-summary span:last-child{border-right:0}.rg-launcher-summary b{display:block;margin-top:5px;color:#172f4f;font-size:12px;text-transform:none}.rg-module-grid{display:grid;grid-template-columns:repeat(3,minmax(250px,1fr));gap:11px}.rg-module-grid>button{min-height:112px;border:1px solid #c5d0da;border-radius:6px;background:#fff;padding:14px;text-align:left;display:grid;grid-template-columns:48px minmax(0,1fr);grid-template-rows:1fr auto;gap:0 12px;color:#12345b;cursor:pointer}.rg-module-grid>button:hover,.rg-module-grid>button.active{border-color:var(--rg-green);background:#f5faf7;box-shadow:0 0 0 1px var(--rg-green)}.rg-module-grid>button>i{grid-row:1/3;width:48px;height:48px;border-radius:5px;background:#edf4ef;color:#166534;border:1px solid #b9d1c1;display:grid;place-items:center;font-style:normal;font-size:11px;font-weight:950}.rg-module-grid>button>div{display:flex;flex-direction:column}.rg-module-grid>button>div b{font-size:14px;line-height:1.25}.rg-module-grid>button>div span{margin-top:5px;color:#536273;font-size:10px;line-height:1.45}.rg-module-grid footer{grid-column:2;display:flex;align-items:center;justify-content:space-between;border-top:1px solid #d5dde5;padding-top:7px;margin-top:7px}.rg-module-grid footer em{font-style:normal;color:#174f87;font-size:9px;font-weight:900}.rg-module-grid footer strong{min-width:25px;padding:4px 7px;border-radius:4px;background:#edf8ef;color:#188142;text-align:center;font-size:9px}.rg-module-bar{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:18px;background:#fff;border:1px solid #b9c5d0;border-top:4px solid var(--rg-green);border-radius:7px;padding:14px 17px}.rg-module-bar>button{border:1px solid #0c2745;border-radius:5px;background:#12345b;color:#fff;padding:10px 13px;font-weight:850;cursor:pointer}.rg-module-bar small{color:var(--rg-green);font-size:9px;font-weight:900;letter-spacing:1px}.rg-module-bar h1{margin:3px 0;color:#12345b;font-size:24px}.rg-module-bar p{margin:0;color:#607083;font-size:11px}.rg-module-bar>strong{border:1px solid #a9cdb5;border-radius:4px;background:#edf6f0;color:#166534;padding:8px 11px;font-size:9px}.rg-page-title{background:#fff;border:1px solid #b9c5d0;border-left:5px solid var(--rg-green);border-radius:6px;padding:18px 20px;margin-bottom:14px}.rg-page-title h1{font-size:24px}.rg-hero{border-radius:7px;box-shadow:none}.rg-panel,.rg-metric{border-radius:6px;box-shadow:none}.rg-panel{border-color:#b9c5d0}.rg-metric{border-color:#b9c5d0}.rg-table-wrap{border-radius:5px}.rg-btn{border-radius:5px;box-shadow:none}
  @media(max-width:1100px){.rg-topbar{padding-inline:20px}.rg-body{padding:18px 20px 34px}.rg-module-grid{grid-template-columns:repeat(2,minmax(230px,1fr))}.rg-launcher-summary{grid-template-columns:repeat(2,1fr)}.rg-launcher-summary span:nth-child(2){border-right:0}.rg-launcher-summary span:nth-child(-n+2){border-bottom:1px solid #cbd4dd}.rg-metrics{grid-template-columns:repeat(2,1fr)}.rg-grid-2{grid-template-columns:1fr}}
  @media(max-width:720px){.rg-topbar{height:auto;padding:9px 12px}.rg-brand img{width:55px;height:54px}.rg-brand b{font-size:17px}.rg-brand span,.rg-session time,.rg-session>div,.rg-live{display:none}.rg-session button{margin:0;padding:9px}.rg-body{padding:13px 12px 28px}.rg-launcher{padding:13px}.rg-launcher-heading{grid-template-columns:62px 1fr;column-gap:11px}.rg-launcher-heading>img{width:60px;height:64px}.rg-launcher-heading h1{font-size:20px}.rg-launcher-summary{grid-template-columns:1fr 1fr}.rg-module-grid{grid-template-columns:1fr}.rg-module-bar{grid-template-columns:1fr;padding:12px}.rg-module-bar>button{width:max-content}.rg-module-bar>strong{width:max-content}.rg-ticker{padding:0 12px}.rg-ticker b{margin-left:-12px;padding:0 9px}.rg-ticker time{display:none}.rg-main{padding-top:13px}.rg-page-title{align-items:flex-start;flex-direction:column}.rg-page-title aside{justify-content:flex-start;margin-top:12px}.rg-page-title h1{font-size:22px}.rg-hero{grid-template-columns:1fr;padding:20px}.rg-hero aside{display:none}.rg-metrics{grid-template-columns:1fr 1fr;gap:9px}.rg-metric{min-height:100px;padding:13px}.rg-filters{grid-template-columns:1fr}.rg-form-grid,.rg-doc-list,.rg-checklist,.rg-summary,.rg-document-grid{grid-template-columns:1fr}.rg-document-grid article{grid-template-columns:30px minmax(0,1fr)}.rg-document-grid article a,.rg-document-grid article em{grid-column:2}.rg-automation-list article{grid-template-columns:70px minmax(0,1fr)}.rg-automation-list article p,.rg-automation-list article time,.rg-automation-list article button{grid-column:2}.rg-workflow{grid-template-columns:repeat(3,1fr);row-gap:18px}.rg-workflow>div:nth-child(4):before{display:none}.rg-activity-list>article{grid-template-columns:30px 1fr}.rg-activity-list time{display:none}.rg-drawer-body{padding:12px}.rg-panel{padding:14px}}
`;
